import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Modal,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const ResidentialPropertyForm = ({ onPropertyAdded }) => {
  const steps = ["Datos Principales", "Características", "Cargar Imágenes", "Descripción"];
  const [activeStep, setActiveStep] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [cities, setCities] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [propertyTypes, setPropertyTypes] = useState([]);

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  const [propertyData, setPropertyData] = useState({
    ubicacion: "",
    lat: null,
    lng: null,
    precio: "",
    tipoPropiedad: "",
    ciudad: "",
    estado: "",
    descripcion: "",
    moneda: "USD",
    ambientes: 0,
    banos: 0,
    dormitorios: 0,
    caracteristicas: {
      // existentes
      metrosCuadrados: 0,
      ambientes: 0,
      banos: 0,
      dormitorios: 0,

      // mismos que en Commercial (todos editables)
      m2Cubiertos: 0,
      m2Semicubiertos: 0,
      m2Descubiertos: 0,
      m2Totales: 0,

      // extras/amenities
      cochera: false,
      mascotas: false,
      pileta: false,
      parrilla: false,
      gimnasio: false,
      laundry: false,
      ascensor: false,
      agua: false,
      cloaca: false,
      gas: false,
      internet: false,
      electricidad: false,
      pavimento: false,
      telefono: false,
      cable: false,
      solarium: false,
      sum: false,
      luminoso: false,
      seguridad: false,
      alumbrado: false,
      terraza: false,
      balcon: false,
      patio: false,
      galeria: false,
      balconTerraza: false,
      jardin: false,
    },
  });

  const caracteristicasLista = [
    "agua", "cloaca", "gas", "internet", "electricidad",
    "pavimento", "telefono", "cable", "gimnasio", "parrilla",
    "solarium", "sum", "pileta", "luminoso",
    "laundry", "seguridad", "alumbrado", "cochera", "mascotas",
    "ascensor", "terraza", "balcon", "patio",
    "galeria", "balconTerraza", "jardin",
  ];

  // Cargar selects
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const { data } = await axios.get("https://backend-admin-nine.vercel.app/api/cities");
        setCities(data);
      } catch (error) {
        console.error("Error al cargar ciudades:", error);
      }
    };

    const fetchPropertyTypes = async () => {
      try {
        const { data } = await axios.get("https://backend-admin-nine.vercel.app/api/types-propiedad");
        setPropertyTypes(data);
      } catch (error) {
        console.error("Error al cargar los tipos de propiedad:", error);
      }
    };

    fetchCities();
    fetchPropertyTypes();
  }, []);

  // Google Places Autocomplete: inicializa SOLO cuando el input existe (paso 0)
  useEffect(() => {
    if (activeStep !== 0) return;
    if (!inputRef.current) return;
    if (!window.google?.maps?.places) return; // asegurate del script con libraries=places

    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["geocode"],
      componentRestrictions: { country: "ar" },
      // fields: ["formatted_address", "geometry"], // recomendado si querés limitar respuesta
    });

    const onPlaceChanged = () => {
      const place = ac.getPlace();
      if (place?.formatted_address && place?.geometry?.location) {
        setPropertyData((prev) => ({
          ...prev,
          ubicacion: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        }));
      }
    };

    ac.addListener("place_changed", onPlaceChanged);
    autocompleteRef.current = ac;

    return () => {
      window.google?.maps?.event?.clearInstanceListeners?.(ac);
    };
  }, [activeStep]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (["metrosCuadrados", "ambientes", "banos", "dormitorios"].includes(name)) {
      setPropertyData((prev) => ({
        ...prev,
        caracteristicas: {
          ...prev.caracteristicas,
          [name]: Number(value),
        },
      }));
      return;
    }

    setPropertyData((prev) => ({
      ...prev,
      [name]:
        name === "precio"
          ? value.replace(/\./g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
          : value,
    }));
  };

  const handleCaracteristicasChange = (e) => {
    const { name, type, value, checked } = e.target;
    setPropertyData((prev) => ({
      ...prev,
      caracteristicas: {
        ...prev.caracteristicas,
        [name]: type === "checkbox" ? checked : Number(value),
      },
    }));
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    setSelectedImages((prev) => [...prev, ...Array.from(files)]);
  };

  const handleImageRemove = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const formData = new FormData();

    const finalPropertyData = {
      ...propertyData,
      tipoPropiedad: propertyData.tipoPropiedad,
    };

    // Validación mínima: o metrosCuadrados o alguno de los m2 detallados
    if (
      (!finalPropertyData.caracteristicas.metrosCuadrados ||
        finalPropertyData.caracteristicas.metrosCuadrados <= 0) &&
      (Number(finalPropertyData.caracteristicas.m2Cubiertos) <= 0 &&
        Number(finalPropertyData.caracteristicas.m2Semicubiertos) <= 0 &&
        Number(finalPropertyData.caracteristicas.m2Descubiertos) <= 0 &&
        Number(finalPropertyData.caracteristicas.m2Totales) <= 0)
    ) {
      setErrors((prev) => ({
        ...prev,
        metrosCuadrados: "Ingresá m² o completa al menos un campo de superficies",
      }));
      setActiveStep(1);
      return;
    }

    formData.append("caracteristicas", JSON.stringify(finalPropertyData.caracteristicas));
    formData.append("ubicacion", finalPropertyData.ubicacion);
    formData.append("lat", finalPropertyData.lat);
    formData.append("lng", finalPropertyData.lng);
    const precioRaw = (finalPropertyData.precio || "").replace(/\./g, "");
    formData.append("precio", precioRaw);
    formData.append("tipoPropiedad", finalPropertyData.tipoPropiedad);
    formData.append("estado", finalPropertyData.estado);
    formData.append("descripcion", finalPropertyData.descripcion);
    formData.append("moneda", finalPropertyData.moneda);
    formData.append("ciudad", finalPropertyData.ciudad);
    formData.append("categoria", "residencial");

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    selectedImages.forEach((file) => {
      if (allowedTypes.includes(file.type)) {
        formData.append("fotos", file);
      } else {
        console.warn("Archivo no permitido:", file.name);
      }
    });

    try {
      await axios.post(
        "https://backend-admin-nine.vercel.app/api/propiedades",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setModalMessage("Propiedad agregada con éxito");
      setOpenModal(true);
      onPropertyAdded?.();
    } catch (error) {
      console.error("Error al agregar la propiedad:", error.response?.data || error);
      setModalMessage("Hubo un error al agregar la propiedad. Intente nuevamente.");
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => setOpenModal(false);

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Datos Principales</Typography>

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Dirección
          </Typography>
          <TextField
            inputRef={inputRef}
            label="Dirección"
            value={propertyData.ubicacion}
            onChange={(e) =>
              setPropertyData((prev) => ({ ...prev, ubicacion: e.target.value }))
            }
            fullWidth
            margin="normal"
            autoComplete="off"
          />

          <TextField
            type="text"
            name="precio"
            label="Valor"
            value={propertyData.precio}
            onChange={handleChange}
            inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
            fullWidth
            margin="normal"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel id="tipo-propiedad-label">Tipo de Propiedad</InputLabel>
            <Select
              labelId="tipo-propiedad-label"
              name="tipoPropiedad"
              value={propertyData.tipoPropiedad || ""}
              onChange={handleChange}
              label="Tipo de Propiedad"
            >
              {propertyTypes.map((type) => (
                <MenuItem key={type._id} value={type._id}>
                  {type.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="estado-label">Estado De la Propiedad</InputLabel>
            <Select
              labelId="estado-label"
              name="estado"
              value={propertyData.estado || ""}
              onChange={handleChange}
              label="Estado De la Propiedad"
            >
              <MenuItem value="Venta">Venta</MenuItem>
              <MenuItem value="Alquiler">Alquiler</MenuItem>
              <MenuItem value="Emprendimiento">Emprendimiento</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="ciudad-label">Ciudad</InputLabel>
            <Select
              labelId="ciudad-label"
              name="ciudad"
              value={propertyData.ciudad || ""}
              onChange={handleChange}
              label="Ciudad"
            >
              {cities.map((city) => (
                <MenuItem key={city._id} value={city._id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="moneda-label">Moneda</InputLabel>
            <Select
              labelId="moneda-label"
              name="moneda"
              value={propertyData.moneda}
              onChange={handleChange}
              label="Moneda"
            >
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="ARS">Pesos Argentinos</MenuItem>
            </Select>
          </FormControl>
        </Box>
      ) : activeStep === 1 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Características</Typography>

          <TextField
            type="number"
            name="ambientes"
            label="Ambientes"
            value={propertyData.caracteristicas.ambientes}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
          />

          <TextField
            key="banos-input"
            type="number"
            name="banos"
            label="Baños"
            value={propertyData.caracteristicas.banos}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
            inputProps={{ autoComplete: "off" }}
          />

          <TextField
            type="number"
            name="dormitorios"
            label="Dormitorios"
            value={propertyData.caracteristicas.dormitorios}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
          />

          <TextField
            type="number"
            name="metrosCuadrados"
            label="Metros Cuadrados"
            value={propertyData.caracteristicas.metrosCuadrados}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
            error={!!errors.metrosCuadrados}
            helperText={errors.metrosCuadrados}
          />

          {/* Campos de superficies (todos editables) */}
          <TextField
            key="m2cubiertos-input"
            type="number"
            name="m2Cubiertos"
            label="M² Cubiertos"
            value={propertyData.caracteristicas.m2Cubiertos}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
          />
          <TextField
            key="m2semicubiertos-input"
            type="number"
            name="m2Semicubiertos"
            label="M² Semicubiertos"
            value={propertyData.caracteristicas.m2Semicubiertos}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
          />
          <TextField
            key="m2descubiertos-input"
            type="number"
            name="m2Descubiertos"
            label="M² Descubiertos"
            value={propertyData.caracteristicas.m2Descubiertos}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
          />
          <TextField
            key="m2totales-input"
            type="number"
            name="m2Totales"
            label="M² Totales"
            value={propertyData.caracteristicas.m2Totales}
            onChange={handleCaracteristicasChange}
            fullWidth
            margin="normal"
          />

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Características Extra</Typography>
            <FormGroup>
              {caracteristicasLista.map((caracteristica) => (
                <FormControlLabel
                  key={caracteristica}
                  control={
                    <Checkbox
                      checked={propertyData.caracteristicas[caracteristica] || false}
                      onChange={handleCaracteristicasChange}
                      name={caracteristica}
                    />
                  }
                  label={
                    caracteristica
                      .charAt(0)
                      .toUpperCase() +
                    caracteristica
                      .slice(1)
                      .replace(/([A-Z])/g, " $1")
                  }
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      ) : activeStep === 2 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Cargar Imágenes</Typography>
          <input type="file" multiple onChange={handleFileChange} />
          <ul>
            {selectedImages.map((foto, index) => (
              <li key={index}>
                {foto.name}{" "}
                <DeleteIcon
                  onClick={() => handleImageRemove(index)}
                  style={{ cursor: "pointer" }}
                />
              </li>
            ))}
          </ul>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Descripción</Typography>
          <TextField
            name="descripcion"
            label="Descripción"
            value={propertyData.descripcion}
            onChange={handleChange}
            fullWidth
            margin="normal"
            multiline
            minRows={3}
          />
        </Box>
      )}

      <Button disabled={activeStep === 0} onClick={() => setActiveStep((p) => p - 1)}>
        Atrás
      </Button>
      <Button onClick={() => (activeStep === steps.length - 1 ? handleSubmit() : setActiveStep((p) => p + 1))}>
        {activeStep === steps.length - 1 ? "Enviar" : "Siguiente"}
      </Button>

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6">{modalMessage}</Typography>
          <Button onClick={handleCloseModal} sx={{ mt: 2 }}>
            Cerrar
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default ResidentialPropertyForm;
